import { action, runInAction } from 'mobx';

import { ApiUrls } from '@app/AppConstants/ApiUrls';
import { UndoOperationModel, DutyItem } from '@app/Models/WebApiModels';

import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import LocalStorageService from '@app/Services/LocalStorageService';
import { dutyChangeService } from '@app/Services/DutyChangeService';
import ApiService from '@app/Services/ApiService';

import { modalService } from '@app/Components/Modal';
import OperationText from '../Pages/Home/Components/OperationText';
import React from 'react';

export type AddUndoOperationHandler = (operationId: string, operationName: string) => void;
export type UndoDutyHandler = () => void;
export type UndoDutyOperationHandler = (operationId: string) => PromiseLike<void>;
export type UndoCountHandler = (count: number) => void;

export class UndoService {
    private _undoProcessing: PromiseCompletion = new PromiseCompletion();
    private _handlerList: UndoCountHandler[] = [];

    public notifyCountChange(count: number) {
        const handlers = this._handlerList.slice();
        for (let i = 0; i < handlers.length; i++) {
            handlers[i](count);
        }
    }

    public subscribe(onChange: UndoCountHandler) {
        if (!this._handlerList.length && !this._undoProcessing.isPending) {
            void this._loadOperations();
        }
        if (this._handlerList.indexOf(onChange) !== -1) {
            throw new Error('Callback is already registered!');
        }
        this._handlerList.push(onChange);
    }

    public unsubscribe(onChange: UndoCountHandler) {
        const callbackIndex = this._handlerList.indexOf(onChange);
        if (callbackIndex === -1) throw new Error('Callback is not registered!');

        this._handlerList.splice(callbackIndex, 1);
    }

    @action
    private async _loadOperations() {
        await this._undoProcessing.add(async () => {
            return window.setTimeout(async () => {
                const operations = (await ApiService.getTypedData<Array<UndoOperationModel>>(ApiUrls.DutyUndoUrl)).data;
                runInAction(() => {
                    const operationsCount = LocalStorageService.addOperationListToStack(operations);
                    this.notifyCountChange(operationsCount);
                });
            }, 1000);
        });
    }

    @action.bound
    public addOperation(operationId: string, operationName: string) {
        const operationsCount = LocalStorageService.addOperationToStack(operationId, operationName);
        this.notifyCountChange(operationsCount);
    }

    @action
    public async undoDuty(suppressConfirmation: boolean, rosterLoading: PromiseCompletion) {
        if (this._undoProcessing.isPending) return;

        const operationDetails = LocalStorageService.pickTopOperationDetails();

        if (!operationDetails?.operationId) return;

        if (!suppressConfirmation) {
            const isConfirmed = await modalService.showConfirmation(
                React.createElement(OperationText, { operationId: operationDetails.operationId, text: operationDetails.operationName }), `Would you like to undo operation?`
            );

            if (!isConfirmed) {
                return;
            }
        }

        await this.undoDutyOperation(operationDetails.operationId, true, rosterLoading);
    }

    @action
    public async undoDutyOperation(operationId: string, removeOperation: boolean = true, rosterLoading: PromiseCompletion, shouldRemoveOperationInErrorCase?: (error: string | null) => Promise<boolean>) {
        const undoRequest = ApiService.postTypedData<{ duties: DutyItem[] | null, operationId: string, errorMessage: string | null }>(ApiUrls.DutyUndoUrl + '?operationId=' + operationId, null, { completion: this._undoProcessing });
        rosterLoading.subscribe(undoRequest);

        const { data: response } = await undoRequest;

        if (!response.errorMessage && response.duties) {
            void dutyChangeService.notifyDutyChange(response.duties);

            if (removeOperation) {
                LocalStorageService.getLastOperation();
            }
        } else {
            const operationDetails = LocalStorageService.pickTopOperationDetails();

            const shouldRemoveOperation = await (shouldRemoveOperationInErrorCase
                ? shouldRemoveOperationInErrorCase(response.errorMessage)
                : modalService.showYesNo(
                    React.createElement("p", {}, [
                        React.createElement(OperationText, { operationId: operationId, text: operationDetails?.operationName ?? "", key: "text" }),
                        React.createElement("br", { key: "br1" }),
                        React.createElement("br", { key: "br2" }),
                        React.createElement("b", { key: "b1" }, `EXCEPTION:`),
                        React.createElement("br", { key: "br3" }),
                        React.createElement("a", { key: "a1", style: { color: "red" } }, `${response.errorMessage}`),
                        React.createElement("br", { key: "br4" }),
                        React.createElement("br", { key: "br5" }),
                        React.createElement("a", { key: "a2" }, `Undo failed. Would you like to delete failed operation from Undo list?`),
                    ]), "Undo Execution Failed"
                ));

            if (!shouldRemoveOperation) {
                return;
            }
            LocalStorageService.getLastOperation();
            await ApiService.postData(ApiUrls.DutyRollbackUndoUrl + '?operationId=' + operationId);
        }
        const operationsCount = LocalStorageService.getOperationsAmount();
        this.notifyCountChange(operationsCount);
    }
}
